import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import useSWR from 'swr';
import dayjs, { type Dayjs } from 'dayjs';
import { Box, styled } from '@mui/material';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Label from '@/components/@IntranetLibrary/Label';
import { RadioGroupV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import Select from '@/components/@IntranetLibrary/Select';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  ApprovalOutlineInput,
  approvalOutlineSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import InfoList from '@/components/Approval/@components/InfoList';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  NewCardAgreeAlert,
  RequiredSectionTitle,
  SectionHint,
} from '@/components/Approval/@components/NewCorporateCard';
import RHFTextArea from '@/components/HookForm/RHFTextarea';
import { getCorporateCardDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  CARD_DIRECT_INPUT,
  CARD_REISSUE_REASONS,
  CARD_REPORT_PENDING_ALERT,
  CARD_REPORT_STATUSES,
  cardDigits,
  formatCardNumber,
  formatLimitInput,
  getCardLimitMaxEndDate,
  isCardLimitIncreaseType,
  limitsOfCard,
  type CardReissueReason,
  type CardReportStatus,
  type CorporateCardDraftSource,
  type CorporateCardRequestFormState,
} from '@/utils/approval/corporateCardRequest';
import { VEHICLE_COST_HR_MISSING_MESSAGE } from '@/utils/approval/vehicleCost';
import { formatAsDate } from '@/utils/format';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const HrMissingNotice = styled(ApprovalNoticeBox)`
  color: var(--dk-danger, #f55060);
`;

const InputColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 440px;
  max-width: 100%;
`;

const InlineRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 16px;
`;

const SubField = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LimitInputRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const DangerText = styled('p')`
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--dk-danger, #f55060);
`;

const toPickerValue = (value: string): Dayjs | null =>
  value ? dayjs(value) : null;

const toDateString = (value: Dayjs | null): string =>
  value?.isValid() ? formatAsDate(value) : '';

type Props = { apvType: number };

/** 법인카드 한도증액(206)·재발급(207) 본문 — 레거시 appr_edit_forms_card_limit·card_reissue */
const CorporateCardRequestField = ({ apvType }: Props) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'corporateCardRequest' });
  const isLimit = isCardLimitIncreaseType(apvType);

  // 레거시처럼 조회 실패는 안내 없이 직접 입력만 남김
  const { data: source, error: sourceError } = useSWR<CorporateCardDraftSource>(
    state ? getCorporateCardDraftSourceUrl(apvType) : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const cards = useMemo(
    () =>
      (source?.display?.cards ?? []).map((card) => ({
        ...card,
        cardNumber: cardDigits(card.cardNumber ?? ''),
      })),
    [source],
  );
  const cardsSettled = !!source || !!sourceError;

  const update = useCallback(
    (patch: Partial<CorporateCardRequestFormState>) => {
      const current = getValues('corporateCardRequest');
      if (!current) return;
      setValue(
        'corporateCardRequest',
        { ...current, ...patch },
        { shouldDirty: true },
      );
    },
    [getValues, setValue],
  );

  // 저장 카드가 목록에 없으면 레거시처럼 직접 입력으로 보여 줌. 폼 값은 그대로 — 바꾸면 이탈 경고·저장값이 달라짐
  const savedListCard =
    state?.cardNumberType === 'list' ? state.cardNumber : '';
  const savedCardMissing =
    cardsSettled &&
    !!savedListCard &&
    !cards.some((card) => card.cardNumber === savedListCard);

  if (!state) {
    return (
      <FieldBlock>
        <HrMissingNotice>{t(VEHICLE_COST_HR_MISSING_MESSAGE)}</HrMissingNotice>
      </FieldBlock>
    );
  }

  const { applicant } = state;
  const isDirect = state.cardNumberType === 'direct' || savedCardMissing;
  const selectValue = isDirect
    ? CARD_DIRECT_INPUT
    : state.cardNumberType === 'list'
    ? state.cardNumber
    : '';
  const listedCards =
    savedListCard && !cardsSettled
      ? [{ cardNumber: savedListCard }]
      : cards.map((card) => ({ cardNumber: card.cardNumber }));

  const handleSelectCard = (value: string) => {
    if (value === CARD_DIRECT_INPUT) {
      update({
        cardNumberType: 'direct',
        cardNumber: '',
        ...(isLimit && { currentLimit: '', requestedLimit: '' }),
      });
      return;
    }
    update({
      cardNumberType: 'list',
      cardNumber: value,
      ...(isLimit &&
        limitsOfCard(cards.find((card) => card.cardNumber === value))),
    });
  };

  const handleStartDate = (value: Dayjs | null) => {
    const startDate = toDateString(value);
    update({ startDate, endDate: startDate });
  };

  const handleReissueReason = (value: CardReissueReason) => {
    update({
      reissueReason: value,
      ...(value !== 'lost' && { reportStatus: '' }),
    });
  };

  return (
    <Box>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('신청자 정보')}</InfoSectionTitle>
        </FieldHeader>
        <InfoList
          rows={[
            { label: t('이름'), value: applicant.name },
            { label: t('사번'), value: applicant.employeeNumber },
            { label: t('소속'), value: applicant.department },
            { label: t('입사일'), value: applicant.hireDate },
          ]}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <RequiredSectionTitle>{t('카드 번호')}</RequiredSectionTitle>
        </FieldHeader>
        <InputColumn>
          <Select
            value={selectValue}
            placeholder={t('카드를 선택해 주세요')}
            options={[
              ...listedCards.map((card) => ({
                value: card.cardNumber,
                label: formatCardNumber(card.cardNumber),
              })),
              { value: CARD_DIRECT_INPUT, label: t('직접 입력') },
            ]}
            onChange={(e) => handleSelectCard(e.target.value as string)}
            sx={approvalOutlineSelectSx}
          />
          {isDirect && (
            <ApprovalOutlineInput
              value={formatCardNumber(state.cardNumber)}
              placeholder={t('숫자만 입력해 주세요')}
              inputProps={{ maxLength: 19, inputMode: 'numeric' }}
              onChange={(e) =>
                update({
                  cardNumberType: 'direct',
                  cardNumber: cardDigits(e.target.value),
                })
              }
            />
          )}
        </InputColumn>
      </FieldBlock>

      {isLimit ? (
        <>
          <FieldBlock>
            <FieldHeader
              sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <RequiredSectionTitle>{t('사용 기간')}</RequiredSectionTitle>
              <SectionHint>
                {t('사용 일수는 워킹데이 기준으로 적용됩니다.')}
                <br />
                {t('한도증액 최대기간은 사용 기간 시작일로부터 3개월입니다.')}
              </SectionHint>
            </FieldHeader>
            <InlineRow>
              <SubField>
                <Label size={14} isRequired={false} title={t('시작일')} />
                <DatePicker
                  value={toPickerValue(state.startDate)}
                  onChange={handleStartDate}
                  format="YYYY-MM-DD"
                  placeholder="yyyy-mm-dd"
                  sx={{ width: 180 }}
                />
              </SubField>
              <Box sx={{ pb: '10px' }}>~</Box>
              <SubField>
                <Label size={14} isRequired={false} title={t('종료일')} />
                <DatePicker
                  value={toPickerValue(state.endDate)}
                  onChange={(value) => update({ endDate: toDateString(value) })}
                  minDate={toPickerValue(state.startDate) ?? undefined}
                  maxDate={
                    state.startDate
                      ? dayjs(getCardLimitMaxEndDate(state.startDate))
                      : undefined
                  }
                  format="YYYY-MM-DD"
                  placeholder="yyyy-mm-dd"
                  sx={{ width: 180 }}
                />
              </SubField>
            </InlineRow>
          </FieldBlock>

          <FieldBlock>
            <FieldHeader>
              <RequiredSectionTitle>{t('금액 한도')}</RequiredSectionTitle>
            </FieldHeader>
            <InlineRow>
              <SubField>
                <Label size={14} isRequired={false} title={t('기본 한도')} />
                <LimitInputRow>
                  <ApprovalOutlineInput
                    value={state.currentLimit}
                    disabled={!isDirect}
                    inputProps={{ inputMode: 'numeric' }}
                    onChange={(e) =>
                      update({ currentLimit: formatLimitInput(e.target.value) })
                    }
                    sx={{ width: 160 }}
                  />
                  {t('만원')}
                </LimitInputRow>
              </SubField>
              <SubField>
                <Label size={14} isRequired={false} title={t('최종 한도')} />
                <LimitInputRow>
                  <ApprovalOutlineInput
                    value={state.requestedLimit}
                    inputProps={{ inputMode: 'numeric' }}
                    onChange={(e) =>
                      update({
                        requestedLimit: formatLimitInput(e.target.value),
                      })
                    }
                    sx={{ width: 160 }}
                  />
                  {t('만원')}
                </LimitInputRow>
              </SubField>
            </InlineRow>
          </FieldBlock>

          <FieldBlock>
            <FieldHeader
              sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <RequiredSectionTitle>{t('사유')}</RequiredSectionTitle>
              <SectionHint>
                {t('증액 사유는 구체적으로 작성합니다.')}
                <br />
                {t(
                  '사전 품의가 있다면 관련 품의에 연결해 주시기 바랍니다. (출장비·광고비·소모품비는 필수)',
                )}
              </SectionHint>
            </FieldHeader>
            <RHFTextArea name="corporateCardRequest.reason" minRows={4} />
          </FieldBlock>
        </>
      ) : (
        <>
          <FieldBlock>
            <FieldHeader
              sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <RequiredSectionTitle>{t('재발급 사유')}</RequiredSectionTitle>
              <DangerText>
                {t(
                  '카드 분실 즉시 국민카드사(1588-1688)로 분실신고 후 재발급 신청해 주세요.',
                )}
                <br />
                {t(
                  '분실신고 지연으로 발생한 모든 문제에 대한 책임은 본인이 질 수 있습니다.',
                )}
              </DangerText>
            </FieldHeader>
            <InlineRow sx={{ alignItems: 'center' }}>
              <RadioGroupV2
                row
                value={state.reissueReason}
                onChange={(e) =>
                  handleReissueReason(e.target.value as CardReissueReason)
                }
                options={CARD_REISSUE_REASONS.map((option) => ({
                  value: option.value,
                  label: t(option.label),
                }))}
                sx={{ gap: '8px' }}
              />
              {state.reissueReason === 'other' && (
                <ApprovalOutlineInput
                  value={state.reasonText}
                  placeholder={t('사유 기재')}
                  onChange={(e) => update({ reasonText: e.target.value })}
                  sx={{ width: 280, maxWidth: '100%' }}
                />
              )}
            </InlineRow>
          </FieldBlock>

          {state.reissueReason === 'lost' && (
            <FieldBlock>
              <FieldHeader>
                <RequiredSectionTitle>
                  {t('분실 신고 여부')}
                </RequiredSectionTitle>
              </FieldHeader>
              <RadioGroupV2
                row
                value={state.reportStatus}
                onChange={(e) =>
                  update({ reportStatus: e.target.value as CardReportStatus })
                }
                options={CARD_REPORT_STATUSES.map((option) => ({
                  value: option.value,
                  label: t(option.label),
                }))}
                sx={{ gap: '8px' }}
              />
              {state.reportStatus === 'pending' && (
                <NewCardAgreeAlert sx={{ mt: 1 }}>
                  {t(CARD_REPORT_PENDING_ALERT)}
                </NewCardAgreeAlert>
              )}
            </FieldBlock>
          )}
        </>
      )}
    </Box>
  );
};

export default CorporateCardRequestField;
